import { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, CheckCircle, PlugsConnected, Warning } from "@phosphor-icons/react";

export default function WixConnect() {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const instance = params.get("instance") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const connect = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/wix/connect", { instance, email: email.trim(), password }, { withCredentials: false });
      setResult(data);
    } catch (e) { setErr(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center px-6 py-10" data-testid="wix-connect-page">
      <LanguageSwitcher className="fixed bottom-4 right-4 z-30" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-2">
          <PlugsConnected size={26} weight="duotone" className="text-gold" />
          <h1 className="text-2xl font-bold tracking-tight">{t("wixconnect.title")}</h1>
        </div>

        {!instance ? (
          <div className="mt-6 border border-amber-500/30 rounded-xl p-5 bg-amber-500/[0.06] flex items-start gap-3" data-testid="wix-connect-no-instance">
            <Warning size={22} className="text-amber-400 shrink-0 mt-0.5" weight="fill" />
            <p className="text-sm text-neutral-300">{t("wixconnect.noInstance")}</p>
          </div>
        ) : result ? (
          <div className="mt-6 border border-emerald-500/25 rounded-xl p-6 bg-emerald-500/[0.06] text-center" data-testid="wix-connect-success">
            <CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto" />
            <h2 className="text-xl font-semibold mt-3">{t("wixconnect.connectedTitle")}</h2>
            <p className="text-neutral-300 text-sm mt-2">{t("wixconnect.connectedBody", { business: result.business })}</p>
            {result.store_slug && <p className="text-neutral-500 text-xs mt-3">{t("wixconnect.storeNote", { slug: result.store_slug })} {result.store_enabled ? "" : t("wixconnect.publishReminder")}</p>}
          </div>
        ) : (
          <>
            <p className="text-neutral-400 text-sm mt-2 mb-6">{t("wixconnect.intro")}</p>
            <div className="space-y-3">
              <div>
                <Label>{t("wixconnect.hephyEmail")}</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-white/5 border-white/15" data-testid="wix-connect-email" />
              </div>
              <div>
                <Label>{t("common.password")}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-white/5 border-white/15" data-testid="wix-connect-password" />
              </div>
              {err && <p className="text-xs text-red-300" data-testid="wix-connect-error">{err}</p>}
              <Button onClick={connect} disabled={busy} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold h-11" data-testid="wix-connect-btn">
                {busy ? <CircleNotch className="animate-spin" /> : t("wixconnect.connectBtn")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
